import React from 'react';
import { Box, Paper, Typography, Avatar, Stack } from '@mui/material';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import PlaceIcon from '@mui/icons-material/Place';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TimelineIcon from '@mui/icons-material/Timeline';

interface DriverActionsPanelProps {
  actionHistory: any[];
}

const META: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
  REST: { label: 'Started a rest', color: '#1565C0', Icon: BedtimeIcon },
  REST_RESUME: { label: 'Resumed from rest', color: '#2E7D32', Icon: PlayCircleFilledIcon },
  PLANNED_STOP: { label: 'Logged a planned stop', color: '#1565C0', Icon: EventAvailableIcon },
  STOP: { label: 'Stopped the transaction', color: '#D32F2F', Icon: StopCircleIcon },
  STOP_RESUME: { label: 'Resumed after stop', color: '#2E7D32', Icon: PlayCircleFilledIcon },
  SWAP: { label: 'Swapped out of the transaction', color: '#757575', Icon: SwapHorizIcon },
  // ── Driver's Component: control handover ──
  DRIVING_CLAIM: { label: 'Took control — driving', color: '#1E8E4E', Icon: DirectionsCarIcon },
  DRIVING_TAKE_CONTROL: { label: 'Took control from another driver', color: '#ED6C02', Icon: PublishedWithChangesIcon },
  DRIVING_RELEASE: { label: 'Reached the destination', color: '#2E7D32', Icon: PlaceIcon },
  DRIVING_FORCE_RELEASE: { label: 'Driving force-released by an admin', color: '#D32F2F', Icon: GppMaybeIcon },
};

const formatTime = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const formatDuration = (ms: number): string => {
  if (!ms || ms < 0) return '0m';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Total time spent on rest / stop, by pairing each start (REST / STOP) with its
 * resume (REST_RESUME / STOP_RESUME) chronologically. A start with no resume =
 * the driver is currently resting/stopped → counted up to now (ongoing).
 */
const computeTotals = (
  actions: any[],
): { restMs: number; stopMs: number; restOpen: boolean; stopOpen: boolean } => {
  const sorted = [...actions]
    .filter((a) => a?.createdAt || a?.updatedAt)
    .sort(
      (a, b) =>
        new Date(a.createdAt || a.updatedAt).getTime() -
        new Date(b.createdAt || b.updatedAt).getTime(),
    );
  let restMs = 0;
  let stopMs = 0;
  let restStart: number | null = null;
  let stopStart: number | null = null;
  for (const a of sorted) {
    const t = new Date(a.createdAt || a.updatedAt).getTime();
    const action = (a?.action || '').toString().toUpperCase();
    if (action === 'REST') restStart = t;
    else if (action === 'REST_RESUME') {
      if (restStart != null) {
        restMs += t - restStart;
        restStart = null;
      }
    } else if (action === 'STOP') stopStart = t;
    else if (action === 'STOP_RESUME') {
      if (stopStart != null) {
        stopMs += t - stopStart;
        stopStart = null;
      }
    }
  }
  const now = Date.now();
  let restOpen = false;
  let stopOpen = false;
  if (restStart != null) {
    restMs += now - restStart;
    restOpen = true;
  }
  if (stopStart != null) {
    stopMs += now - stopStart;
    stopOpen = true;
  }
  return { restMs, stopMs, restOpen, stopOpen };
};

/**
 * Renders the driver Rest / Stop / Swap audit trail on the transaction detail
 * page. Reads `transactionDetails.actionHistory[]` (returned by the history
 * details endpoint) — kept separate from the FORM|INSTRUCTION step history.
 */
const DriverActionsPanel: React.FC<DriverActionsPanelProps> = ({ actionHistory }) => {
  if (!Array.isArray(actionHistory) || actionHistory.length === 0) return null;

  // Show latest activity first. computeTotals keeps its own chronological sort
  // for REST/STOP duration pairing, so this display-only sort is independent.
  const displayHistory = [...actionHistory].sort(
    (a, b) =>
      new Date(b?.createdAt || b?.updatedAt || 0).getTime() -
      new Date(a?.createdAt || a?.updatedAt || 0).getTime(),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TimelineIcon fontSize="small" sx={{ color: '#455A64' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
          DRIVER ACTIVITY
        </Typography>
      </Box>

      {(() => {
        const { restMs, stopMs, restOpen, stopOpen } = computeTotals(actionHistory);
        if (restMs <= 0 && stopMs <= 0) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              mb: 1.5,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {restMs > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <BedtimeIcon fontSize="small" sx={{ color: '#1565C0' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total rest: {formatDuration(restMs)}
                  {restOpen ? ' (ongoing)' : ''}
                </Typography>
              </Box>
            )}
            {stopMs > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <StopCircleIcon fontSize="small" sx={{ color: '#D32F2F' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total stop: {formatDuration(stopMs)}
                  {stopOpen ? ' (ongoing)' : ''}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })()}

      <Stack spacing={1.5}>
        {displayHistory.map((a: any, idx: number) => {
          const meta = META[(a?.action || '').toUpperCase()] || {
            label: a?.action || 'Action',
            color: '#9e9e9e',
            Icon: TimelineIcon,
          };
          const Icon = meta.Icon;
          const reason = (a?.reason || '').toString();
          const reasonNote = (a?.reasonNote || '').toString();
          const plannedAt = a?.plannedAt;
          const files: any[] = Array.isArray(a?.files) ? a.files : [];

          return (
            <Box key={a?.uid || idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Avatar sx={{ bgcolor: `${meta.color}1A`, color: meta.color, width: 32, height: 32 }}>
                <Icon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {(a?.userName || 'Driver')} — {meta.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(a?.createdAt || a?.updatedAt)}
                </Typography>
                {reason ? (
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                    Reason: {reason}
                  </Typography>
                ) : null}
                {reasonNote ? (
                  <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
                    Note: {reasonNote}
                  </Typography>
                ) : null}
                {plannedAt && formatTime(plannedAt) ? (
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                    Planned for: {formatTime(plannedAt)}
                  </Typography>
                ) : null}
                {files.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {files.map((f: any, fi: number) => {
                      const url = f?.url || f?.signedUrl || f?.public_url;
                      if (!url) return null;
                      // Named evidence slots stamp the title onto the descriptor.
                      // Evidence captured before slots existed has none, so it
                      // falls back to a position rather than an empty caption.
                      const label = (f?.label || '').toString().trim() || `Photo ${fi + 1}`;
                      return (
                        <Box key={fi} sx={{ width: 72 }}>
                          <Box
                            component="img"
                            src={url}
                            alt={label}
                            title={label}
                            onClick={() => window.open(url, '_blank', 'noopener')}
                            sx={{
                              width: 56,
                              height: 56,
                              objectFit: 'cover',
                              borderRadius: 1,
                              cursor: 'pointer',
                              border: '1px solid #e0e0e0',
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.2, mt: 0.25, wordBreak: 'break-word' }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default DriverActionsPanel;
